package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.TextButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.i18n.strings
import org.gymly.model.AdminMe
import org.gymly.model.AdminMeUpsert
import org.gymly.model.AdminPersonSex
import org.gymly.model.AdminUser
import org.gymly.model.AdminWeekday
import org.gymly.model.AdminWorkDaySchedule
import org.gymly.model.PickedImagePayload
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.PhotoActionBar
import org.gymly.util.imagePreviewModel
import org.gymly.util.openImagePicker
import org.gymly.util.userMessage
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.ui.components.PasswordRulesHint
import org.gymly.ui.components.PasswordVisibilityToggle
import org.gymly.ui.components.rememberPasswordVisible

@Composable
fun AdminProfileScreen(
    user: AdminUser,
    onUserUpdated: (AdminUser, ByteArray?) -> Unit,
) {
    val t = strings()
    val scope = rememberCoroutineScope()
    var me by remember { mutableStateOf<AdminMe?>(null) }
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var uploadingPhoto by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var success by remember { mutableStateOf<String?>(null) }
    var photoRevision by remember { mutableStateOf(0) }

    var firstName by remember { mutableStateOf(user.displayName.substringBefore(' ')) }
    var lastName by remember { mutableStateOf(user.displayName.substringAfter(' ', "")) }
    var email by remember { mutableStateOf(user.email.orEmpty()) }
    var phone by remember { mutableStateOf("") }
    var docId by remember { mutableStateOf("") }
    var address by remember { mutableStateOf("") }
    var sex by remember { mutableStateOf<AdminPersonSex?>(null) }
    // Aviso de cambios: se compara con los datos ya guardados (se actualiza al cargar el perfil).
    fun profileSnapshot(): String = listOf(firstName, lastName, email, phone, docId, address, sex?.name.orEmpty()).joinToString("|")
    var savedSnapshot by remember { mutableStateOf<String?>(null) }
    LaunchedEffect(me) {
        if (me != null) savedSnapshot = profileSnapshot()
    }
    var currentPassword by remember { mutableStateOf("") }
    var newPassword by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    val hasChanges = savedSnapshot != null && (
        profileSnapshot() != savedSnapshot ||
            currentPassword.isNotEmpty() || newPassword.isNotEmpty() || confirmPassword.isNotEmpty()
    )
    var preferredLocale by remember { mutableStateOf(user.preferredLocale ?: "es") }
    var nameError by remember { mutableStateOf<String?>(null) }
    var addressError by remember { mutableStateOf<String?>(null) }
    var sexError by remember { mutableStateOf<String?>(null) }
    var pendingPhoto by remember { mutableStateOf<PickedImagePayload?>(null) }
    var pendingPhotoName by remember { mutableStateOf<String?>(null) }
    var photoBytes by remember { mutableStateOf<ByteArray?>(null) }
    val photoHolder = remember { org.gymly.data.PickedMediaHolder() }

    fun pushUser(m: AdminMe, bytes: ByteArray?) {
        onUserUpdated(
            AdminUser(
                id = m.id,
                displayName = m.displayName,
                email = m.email,
                role = m.role,
                photoUrl = m.photoUrl,
                preferredLocale = m.preferredLocale,
                photoRevision = photoRevision,
                membershipOverride = m.membershipOverride ||
                    m.role.equals("ADMIN", ignoreCase = true) ||
                    user.membershipOverride,
                permissions = m.permissions,
                permissionsKnown = true,
            ),
            bytes,
        )
    }

    fun applyMe(m: AdminMe, bytes: ByteArray? = photoBytes) {
        me = m
        firstName = m.firstName
        lastName = m.lastName
        email = m.email.orEmpty()
        phone = m.phone.orEmpty()
        docId = m.docId.orEmpty()
        address = m.address.orEmpty()
        sex = m.sex
        preferredLocale = m.preferredLocale ?: user.preferredLocale ?: "es"
        pushUser(m, bytes)
    }

    /** Cambia el idioma del panel al instante y lo guarda en el servidor (sin el resto del perfil). */
    fun applyLocale(code: String) {
        preferredLocale = code
        val current = me
        if (current != null) {
            me = current.copy(preferredLocale = code)
            pushUser(me!!, photoBytes)
        } else {
            onUserUpdated(user.copy(preferredLocale = code), null)
        }
        scope.launch {
            try {
                val updated = AdminRepo.api.updateMyLocale(code)
                // Conservar el idioma elegido si el servidor responde con otro valor nulo.
                me = updated.copy(preferredLocale = updated.preferredLocale ?: code)
                preferredLocale = me!!.preferredLocale ?: code
                pushUser(me!!, photoBytes)
            } catch (e: Exception) {
                error = e.userMessage(t.profile.profileSaveFailed)
            }
        }
    }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                applyMe(AdminRepo.api.fetchMe())
            } catch (e: Exception) {
                error = e.userMessage(t.profile.profileLoadFailed)
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(me?.photoUrl, photoRevision) {
        val url = me?.photoUrl
        if (url.isNullOrBlank()) {
            if (pendingPhoto == null) photoBytes = null
            return@LaunchedEffect
        }
        try {
            val fetched = AdminRepo.api.fetchMyPhotoBytes()
            if (fetched != null && fetched.isNotEmpty()) {
                photoBytes = fetched
                me?.let { pushUser(it.copy(preferredLocale = preferredLocale), fetched) }
            }
        } catch (_: Exception) {
            // Mantener preview local si la hay.
        }
    }

    fun pickPhoto() {
        error = null
        success = null
        openImagePicker { picked ->
            if (picked == null) {
                photoHolder.clear()
                scope.launch { error = t.profile.noImageSelected }
                return@openImagePicker
            }
            val payload = PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType)
            photoHolder.set(payload, picked.fileName)
            scope.launch {
                pendingPhoto = payload
                pendingPhotoName = picked.fileName
                photoBytes = payload.bytes
                photoRevision += 1
                uploadingPhoto = true
                try {
                    val updated = AdminRepo.api.uploadMyPhoto(payload)
                    applyMe(updated, payload.bytes)
                    if (updated.photoUrl.isNullOrBlank()) {
                        error = t.profile.photoNotSaved
                        return@launch
                    }
                    photoHolder.clear()
                    pendingPhoto = null
                    pendingPhotoName = null
                    runCatching { AdminRepo.api.syncStaffAccess(updated.id) }
                    success = t.profile.photoUpdated
                } catch (e: Exception) {
                    error = e.userMessage(t.profile.photoUploadFailed)
                } finally {
                    uploadingPhoto = false
                }
            }
        }
    }

    fun clearPhoto() {
        scope.launch {
            uploadingPhoto = true
            error = null
            success = null
            try {
                pendingPhoto = null
                pendingPhotoName = null
                photoBytes = null
                photoRevision += 1
                AdminRepo.api.deleteMyPhoto()
                applyMe(AdminRepo.api.fetchMe(), null)
                success = t.profile.photoDeleted
            } catch (e: Exception) {
                error = e.userMessage(t.profile.photoDeleteFailed)
            } finally {
                uploadingPhoto = false
            }
        }
    }

    fun saveProfile() {
        scope.launch {
            saving = true
            error = null
            success = null
            nameError = null
            addressError = null
            sexError = null
            try {
                val first = firstName.trim()
                val last = lastName.trim()
                if (first.isEmpty() || last.isEmpty()) {
                    val msg = t.profile.nameRequiredError
                    nameError = msg
                    error = msg
                    return@launch
                }
                val addr = address.trim()
                if (addr.isEmpty()) {
                    val msg = t.profile.addressRequiredError
                    addressError = msg
                    error = msg
                    return@launch
                }
                val selectedSex = sex
                if (selectedSex == null) {
                    val msg = t.profile.sexRequiredError
                    sexError = msg
                    error = msg
                    return@launch
                }
                val fullName = "$first $last".trim()
                val pending = photoHolder.payload ?: pendingPhoto
                if (pending != null) {
                    uploadingPhoto = true
                    try {
                        val withPhoto = AdminRepo.api.uploadMyPhoto(pending)
                        photoRevision += 1
                        photoBytes = pending.bytes
                        applyMe(withPhoto, pending.bytes)
                        if (withPhoto.photoUrl.isNullOrBlank()) {
                            error = t.profile.photoNotSavedRetry
                            return@launch
                        }
                        photoHolder.clear()
                        pendingPhoto = null
                        pendingPhotoName = null
                    } finally {
                        uploadingPhoto = false
                    }
                }
                val updated = AdminRepo.api.updateMe(
                    AdminMeUpsert(
                        displayName = fullName,
                        email = email.trim().takeIf { it.isNotEmpty() },
                        preferredLocale = preferredLocale,
                        phone = phone.trim().takeIf { it.isNotEmpty() },
                        docId = docId.trim().takeIf { it.isNotEmpty() },
                        address = addr,
                        sex = selectedSex,
                    ),
                )
                applyMe(updated, photoBytes)

                val wantsPassword = currentPassword.isNotBlank() ||
                    newPassword.isNotBlank() ||
                    confirmPassword.isNotBlank()
                if (wantsPassword) {
                    when {
                        currentPassword.isBlank() -> {
                            error = t.profile.needCurrentPassword
                            return@launch
                        }
                        newPassword.length < 8 -> {
                            error = t.profile.passwordTooShort
                            return@launch
                        }
                        newPassword != confirmPassword -> {
                            error = t.profile.passwordsDoNotMatch
                            return@launch
                        }
                        else -> {
                            AdminRepo.api.changeMyPassword(currentPassword, newPassword)
                            currentPassword = ""
                            newPassword = ""
                            confirmPassword = ""
                            success = if (pending != null) {
                                t.profile.savedPhotoAndPassword
                            } else {
                                t.profile.savedProfileAndPassword
                            }
                        }
                    }
                } else {
                    success = if (pending != null) t.profile.savedPhotoAndProfile else t.profile.savedProfile
                }
            } catch (e: Exception) {
                error = e.userMessage(t.profile.profileSaveFailed)
            } finally {
                saving = false
                uploadingPhoto = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface)
            .padding(GymlySpace.lg),
    ) {
        val sideBySide = maxWidth >= 820.dp
        // Ancho máximo: un formulario de perfil legible no necesita toda la pantalla.
        Column(Modifier.fillMaxSize().widthIn(max = 960.dp)) {
        VerticalScrollColumn(Modifier.weight(1f).fillMaxWidth()) {
            Text(t.profile.title, fontWeight = FontWeight.Bold, fontSize = 24.sp, color = AdminColors.Text)
            Text(
                t.profile.subtitle,
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp, bottom = 16.dp),
            )
            error?.let {
                Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 10.dp))
            }
            success?.let {
                Text(it, color = Color(0xFF2E7D32), fontSize = 13.sp, modifier = Modifier.padding(bottom = 10.dp))
            }

            if (loading && me == null) {
                Box(Modifier.fillMaxWidth().padding(40.dp), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = AdminColors.Primary)
                }
                return@VerticalScrollColumn
            }

            if (sideBySide) {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(20.dp),
                    verticalAlignment = Alignment.Top,
                ) {
                    ProfilePhotoCard(
                        me = me,
                        photoRevision = photoRevision,
                        uploadingPhoto = uploadingPhoto,
                        photoBytes = photoBytes ?: photoHolder.payload?.bytes ?: pendingPhoto?.bytes,
                        pendingFileName = pendingPhotoName,
                        onPickPhoto = { pickPhoto() },
                        onClearPhoto = { clearPhoto() },
                        modifier = Modifier.width(280.dp),
                    )
                    ProfileFormCard(
                        me = me,
                        firstName = firstName,
                        onFirstName = {
                            firstName = it
                            nameError = null
                            error = null
                        },
                        lastName = lastName,
                        onLastName = {
                            lastName = it
                            nameError = null
                            error = null
                        },
                        email = email,
                        onEmail = { email = it },
                        phone = phone,
                        onPhone = { phone = it },
                        docId = docId,
                        onDocId = { docId = it },
                        address = address,
                        onAddress = {
                            address = it
                            addressError = null
                            error = null
                        },
                        sex = sex,
                        onSex = {
                            sex = it
                            sexError = null
                            error = null
                        },
                        preferredLocale = preferredLocale,
                        onPreferredLocale = { applyLocale(it) },
                        currentPassword = currentPassword,
                        onCurrentPassword = { currentPassword = it },
                        newPassword = newPassword,
                        onNewPassword = { newPassword = it },
                        confirmPassword = confirmPassword,
                        onConfirmPassword = { confirmPassword = it },
                        nameError = nameError,
                        addressError = addressError,
                        sexError = sexError,
                        formError = error,
                        formSuccess = success,
                        saving = saving || uploadingPhoto,
                        onSave = { saveProfile() },
                        modifier = Modifier.weight(1f),
                    )
                }
            } else {
                ProfilePhotoCard(
                    me = me,
                    photoRevision = photoRevision,
                    uploadingPhoto = uploadingPhoto,
                    photoBytes = photoBytes ?: photoHolder.payload?.bytes ?: pendingPhoto?.bytes,
                    pendingFileName = pendingPhotoName,
                    onPickPhoto = { pickPhoto() },
                    onClearPhoto = { clearPhoto() },
                    modifier = Modifier.fillMaxWidth(),
                )
                Spacer(Modifier.height(16.dp))
                ProfileFormCard(
                    me = me,
                    firstName = firstName,
                    onFirstName = {
                        firstName = it
                        nameError = null
                        error = null
                    },
                    lastName = lastName,
                    onLastName = {
                        lastName = it
                        nameError = null
                        error = null
                    },
                    email = email,
                    onEmail = { email = it },
                    phone = phone,
                    onPhone = { phone = it },
                    docId = docId,
                    onDocId = { docId = it },
                    address = address,
                    onAddress = {
                        address = it
                        addressError = null
                        error = null
                    },
                    sex = sex,
                    onSex = {
                        sex = it
                        sexError = null
                        error = null
                    },
                    preferredLocale = preferredLocale,
                    onPreferredLocale = { applyLocale(it) },
                    currentPassword = currentPassword,
                    onCurrentPassword = { currentPassword = it },
                    newPassword = newPassword,
                    onNewPassword = { newPassword = it },
                    confirmPassword = confirmPassword,
                    onConfirmPassword = { confirmPassword = it },
                    nameError = nameError,
                    addressError = addressError,
                    sexError = sexError,
                    formError = error,
                    formSuccess = success,
                    saving = saving || uploadingPhoto,
                    onSave = { saveProfile() },
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }
        ProfileSaveBar(
            formError = error,
            formSuccess = success,
            saving = saving || uploadingPhoto,
            hasChanges = hasChanges,
            onSave = { saveProfile() },
        )
        }
    }
}

@Composable
private fun ProfilePhotoCard(
    me: AdminMe?,
    photoRevision: Int,
    uploadingPhoto: Boolean,
    photoBytes: ByteArray?,
    pendingFileName: String?,
    onPickPhoto: () -> Unit,
    onClearPhoto: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val t = strings()
    val hasPhoto = !me?.photoUrl.isNullOrBlank() || (photoBytes != null && photoBytes.isNotEmpty())
    // Data-URI: Coil en JS/Wasm no decodifica bien ByteArray crudo; la URL /photo necesita JWT.
    val previewModel = imagePreviewModel(photoBytes)

    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(14.dp))
            .padding(horizontal = 22.dp, vertical = 28.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            Modifier
                .size(120.dp)
                .clip(CircleShape)
                .background(AdminColors.Outline.copy(alpha = 0.25f))
                .border(2.dp, AdminColors.Outline.copy(alpha = 0.4f), CircleShape)
                .clickable(enabled = !uploadingPhoto, onClick = onPickPhoto),
            contentAlignment = Alignment.Center,
        ) {
            if (previewModel != null) {
                key(photoRevision, previewModel.takeLast(32)) {
                    AsyncImage(
                        model = previewModel,
                        contentDescription = t.profile.profilePhoto,
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Crop,
                    )
                }
            } else {
                Text(
                    initials(me?.displayName),
                    fontWeight = FontWeight.Bold,
                    fontSize = 36.sp,
                    color = AdminColors.Primary,
                )
            }
            if (uploadingPhoto) {
                Box(
                    Modifier
                        .fillMaxSize()
                        .background(Color.Black.copy(alpha = 0.35f)),
                    contentAlignment = Alignment.Center,
                ) {
                    CircularProgressIndicator(Modifier.size(28.dp), color = Color.White, strokeWidth = 2.dp)
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        PhotoActionBar(
            hasPhoto = hasPhoto,
            enabled = !uploadingPhoto,
            onUpload = onPickPhoto,
            onRemove = if (hasPhoto) onClearPhoto else null,
            accent = AdminColors.Primary,
            error = AdminColors.Error,
            uploadTooltip = if (hasPhoto) t.profile.changePhoto else t.profile.uploadPhoto,
            removeTooltip = t.profile.removePhoto,
        )
        if (!pendingFileName.isNullOrBlank()) {
            Spacer(Modifier.height(8.dp))
            Text(
                t.profile.selectedFile(pendingFileName),
                color = AdminColors.Primary,
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium,
            )
            Text(
                t.profile.photoSaveHint,
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
        }
        Spacer(Modifier.height(16.dp))
        Text(
            me?.displayName?.ifBlank { t.common.emptyValue } ?: t.common.emptyValue,
            fontWeight = FontWeight.Bold,
            fontSize = 18.sp,
            color = AdminColors.Text,
        )
        Spacer(Modifier.height(4.dp))
        Text(
            me?.roleLabel ?: t.common.emptyValue,
            color = AdminColors.TextMuted,
            fontSize = 13.sp,
        )
    }
}

@Composable
private fun ProfileSection(title: String, hint: String? = null, content: @Composable ColumnScope.() -> Unit) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(14.dp))
            .padding(20.dp),
    ) {
        Text(title, fontWeight = FontWeight.SemiBold, fontSize = 16.sp, color = AdminColors.Text)
        hint?.let {
            Text(it, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(top = 4.dp))
        }
        Spacer(Modifier.height(14.dp))
        content()
    }
}

@Composable
private fun ProfileSaveBar(
    formError: String?,
    formSuccess: String?,
    saving: Boolean,
    hasChanges: Boolean,
    onSave: () -> Unit,
) {
    val t = strings()
    Column(
        Modifier
            .fillMaxWidth()
            .padding(top = 12.dp)
            .background(AdminColors.Surface)
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(14.dp))
            .padding(horizontal = 20.dp, vertical = 12.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        formError?.let {
            Text(it, color = AdminColors.Error, fontSize = 13.sp, fontWeight = FontWeight.Medium)
        }
        formSuccess?.let {
            Text(it, color = Color(0xFF2E7D32), fontSize = 13.sp, fontWeight = FontWeight.Medium)
        }
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Text(
                if (hasChanges) "Tienes cambios sin guardar." else "Todos los cambios están guardados.",
                color = if (hasChanges) Color(0xFFE65100) else AdminColors.TextMuted,
                fontSize = 13.sp,
                fontWeight = if (hasChanges) FontWeight.SemiBold else FontWeight.Normal,
                modifier = Modifier.weight(1f),
            )
            Button(
                onClick = onSave,
                enabled = !saving,
                colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Primary),
                shape = RoundedCornerShape(10.dp),
            ) {
                if (saving) {
                    CircularProgressIndicator(Modifier.size(18.dp), color = Color.White, strokeWidth = 2.dp)
                } else {
                    Text(t.common.saveChanges, fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}

@Composable
private fun ProfileFormCard(
    me: AdminMe?,
    firstName: String,
    onFirstName: (String) -> Unit,
    lastName: String,
    onLastName: (String) -> Unit,
    email: String,
    onEmail: (String) -> Unit,
    phone: String,
    onPhone: (String) -> Unit,
    docId: String,
    onDocId: (String) -> Unit,
    address: String,
    onAddress: (String) -> Unit,
    sex: AdminPersonSex?,
    onSex: (AdminPersonSex) -> Unit,
    preferredLocale: String,
    onPreferredLocale: (String) -> Unit,
    currentPassword: String,
    onCurrentPassword: (String) -> Unit,
    newPassword: String,
    onNewPassword: (String) -> Unit,
    confirmPassword: String,
    onConfirmPassword: (String) -> Unit,
    nameError: String? = null,
    addressError: String? = null,
    sexError: String? = null,
    formError: String? = null,
    formSuccess: String? = null,
    saving: Boolean,
    onSave: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val t = strings()
    var sexMenu by remember { mutableStateOf(false) }
    // La contraseña se cambia pocas veces: queda colapsada salvo que ya haya algo escrito.
    var passwordOpen by remember {
        mutableStateOf(currentPassword.isNotEmpty() || newPassword.isNotEmpty() || confirmPassword.isNotEmpty())
    }

    Column(modifier, verticalArrangement = Arrangement.spacedBy(16.dp)) {
        ProfileSection(t.profile.personalData) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                Column(Modifier.weight(1f)) {
                    FieldLabel(t.profile.firstNameRequired)
                    ProfileField(firstName, onFirstName, t.profile.firstNamePlaceholder, isError = nameError != null)
                }
                Column(Modifier.weight(1f)) {
                    FieldLabel(t.profile.lastNameRequired)
                    ProfileField(lastName, onLastName, t.profile.lastNamePlaceholder, isError = nameError != null)
                }
            }
            nameError?.let { FieldError(it) }
            Spacer(Modifier.height(12.dp))
            FieldLabel(t.common.email)
            ProfileField(email, onEmail, t.profile.emailPlaceholder)
            Spacer(Modifier.height(12.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                Column(Modifier.weight(1f)) {
                    FieldLabel(t.common.phone)
                    ProfileField(phone, onPhone, t.profile.phonePlaceholder)
                }
                Column(Modifier.weight(1f)) {
                    FieldLabel(t.common.document)
                    ProfileField(docId, onDocId, t.profile.documentPlaceholder)
                }
            }
            Spacer(Modifier.height(12.dp))
            FieldLabel(t.profile.addressRequired)
            ProfileField(address, onAddress, t.profile.addressPlaceholder, isError = addressError != null)
            addressError?.let { FieldError(it) }
            Spacer(Modifier.height(12.dp))
            FieldLabel(t.profile.sexRequired)
            Box(Modifier.widthIn(max = 280.dp)) {
                OutlinedButton(
                    onClick = { sexMenu = true },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(10.dp),
                    border = androidx.compose.foundation.BorderStroke(
                        1.dp,
                        if (sexError != null) AdminColors.Error else AdminColors.Outline,
                    ),
                ) {
                    Text(
                        sex?.let { t.common.sexLabel(it) } ?: t.common.select,
                        color = if (sexError != null) AdminColors.Error else AdminColors.Text,
                    )
                }
                DropdownMenu(expanded = sexMenu, onDismissRequest = { sexMenu = false }) {
                    AdminPersonSex.entries.forEach { option ->
                        DropdownMenuItem(
                            text = { Text(t.common.sexLabel(option)) },
                            onClick = {
                                onSex(option)
                                sexMenu = false
                            },
                        )
                    }
                }
            }
            sexError?.let { FieldError(it) }
        }

        ProfileSection(t.profile.language, hint = t.profile.languageSavedHint) {
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                LocaleChip(t.profile.spanish, preferredLocale.lowercase() != "en") { onPreferredLocale("es") }
                LocaleChip(t.profile.english, preferredLocale.lowercase() == "en") { onPreferredLocale("en") }
            }
        }

        ProfileSection(t.profile.workSchedule) {
            ProfileWorkSchedule(
                schedule = me?.workSchedule.orEmpty(),
                emptyLabel = t.profile.workScheduleEmpty,
                offDayLabel = t.profile.workScheduleOffDay,
                locale = preferredLocale,
            )
        }

        ProfileSection(t.profile.workData) {
            ProfileReadOnlyRow(t.profile.role, me?.roleLabel ?: t.common.emptyValue)
            ProfileReadOnlyRow(t.common.status, if (me?.active == true) t.common.active else t.common.inactive)
            me?.createdAt?.take(10)?.let { ProfileReadOnlyRow(t.profile.joinedAt, it) }
        }

        ProfileSection(t.profile.changePassword, hint = t.profile.changePasswordHint) {
            if (!passwordOpen) {
                OutlinedButton(
                    onClick = { passwordOpen = true },
                    shape = RoundedCornerShape(10.dp),
                ) {
                    Text(t.profile.changePassword, color = AdminColors.Primary)
                }
            } else {
                FieldLabel(t.profile.currentPassword)
                ProfileField(currentPassword, onCurrentPassword, "••••••••", password = true)
                Spacer(Modifier.height(12.dp))
                FieldLabel(t.profile.newPassword)
                ProfileField(newPassword, onNewPassword, t.profile.newPasswordPlaceholder, password = true)
                PasswordRulesHint(newPassword, muted = AdminColors.TextMuted)
                GeneratePasswordButton(
                    value = newPassword,
                    onGenerated = {
                        onNewPassword(it)
                        onConfirmPassword(it)
                    },
                    onClear = {
                        onNewPassword("")
                        onConfirmPassword("")
                    },
                )
                Spacer(Modifier.height(12.dp))
                FieldLabel(t.profile.confirmNewPassword)
                ProfileField(confirmPassword, onConfirmPassword, t.profile.confirmPasswordPlaceholder, password = true)
                Spacer(Modifier.height(8.dp))
                TextButton(onClick = {
                    onCurrentPassword("")
                    onNewPassword("")
                    onConfirmPassword("")
                    passwordOpen = false
                }) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
            }
        }
    }
}

@Composable
private fun ProfileReadOnlyRow(label: String, value: String) {
    Row(Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
        Text(label, Modifier.width(110.dp), color = AdminColors.TextMuted, fontSize = 13.sp)
        Text(
            value,
            color = AdminColors.Text,
            fontSize = 13.sp,
            fontWeight = FontWeight.Medium,
            modifier = Modifier.weight(1f),
        )
    }
}

@Composable
private fun ProfileWorkSchedule(
    schedule: List<AdminWorkDaySchedule>,
    emptyLabel: String,
    offDayLabel: String,
    locale: String,
) {
    if (schedule.isEmpty()) {
        Text(
            emptyLabel,
            color = AdminColors.TextMuted,
            fontSize = 13.sp,
            modifier = Modifier.padding(bottom = 4.dp),
        )
        return
    }
    val useEs = locale.lowercase() != "en"
    // Dos días por fila: la jornada ocupa menos alto.
    AdminWeekday.entries.chunked(2).forEach { pair ->
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.Top,
        ) {
        pair.forEach { day ->
        val daySchedule = schedule.firstOrNull { it.weekday == day.iso }
        val enabled = daySchedule != null && daySchedule.blocks.isNotEmpty()
        Column(
            Modifier
                .weight(1f)
                .padding(bottom = 8.dp)
                .clip(RoundedCornerShape(12.dp))
                .border(
                    1.dp,
                    if (enabled) AdminColors.Primary.copy(alpha = 0.35f)
                    else AdminColors.Outline.copy(alpha = 0.35f),
                    RoundedCornerShape(12.dp),
                )
                .background(
                    if (enabled) AdminColors.Primary.copy(alpha = 0.04f)
                    else AdminColors.Surface.copy(alpha = 0.5f),
                )
                .padding(10.dp),
        ) {
            Text(
                if (useEs) day.labelEs else day.labelEn,
                fontWeight = FontWeight.SemiBold,
                fontSize = 13.sp,
                color = AdminColors.Text,
            )
            Spacer(Modifier.height(4.dp))
            if (!enabled) {
                Text(offDayLabel, color = AdminColors.TextMuted, fontSize = 12.sp)
            } else {
                daySchedule!!.blocks.forEach { block ->
                    Text(
                        "${block.startTime.trim().take(5)} – ${block.endTime.trim().take(5)}",
                        color = AdminColors.Text,
                        fontSize = 13.sp,
                        modifier = Modifier.padding(top = 2.dp),
                    )
                }
            }
        }
        }
        if (pair.size == 1) Spacer(Modifier.weight(1f))
        }
    }
}

@Composable
private fun LocaleChip(label: String, selected: Boolean, onClick: () -> Unit) {
    Text(
        label,
        color = if (selected) AdminColors.OnBrand else AdminColors.Text,
        fontSize = 13.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(16.dp))
            .background(if (selected) AdminColors.Primary else AdminColors.Surface)
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(16.dp))
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 8.dp),
    )
}

@Composable
private fun FieldLabel(text: String) {
    Text(text, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(bottom = 4.dp))
}

@Composable
private fun FieldError(text: String) {
    Text(
        text,
        color = AdminColors.Error,
        fontSize = 12.sp,
        modifier = Modifier.padding(top = 4.dp),
    )
}

@Composable
private fun ProfileField(
    value: String,
    onChange: (String) -> Unit,
    placeholder: String,
    password: Boolean = false,
    isError: Boolean = false,
) {
    val (visible, toggleVisible) = rememberPasswordVisible()
    OutlinedTextField(
        value = value,
        onValueChange = onChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        isError = isError,
        placeholder = { Text(placeholder, color = AdminColors.TextMuted) },
        visualTransformation = if (password && !visible) PasswordVisualTransformation() else androidx.compose.ui.text.input.VisualTransformation.None,
        trailingIcon = if (password) {
            { PasswordVisibilityToggle(visible = visible, onToggle = toggleVisible) }
        } else {
            null
        },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
            errorBorderColor = AdminColors.Error,
        ),
    )
}

private fun initials(name: String?): String =
    name.orEmpty()
        .split(" ")
        .mapNotNull { it.firstOrNull()?.uppercaseChar() }
        .take(2)
        .joinToString("")
        .ifBlank { "A" }
